import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import FileUpload from 'Components/FileUpload';
import Modal, { ModalBody, ModalFooter } from 'Components/Modal';
// Material UI
import { Grid, Alert } from '@mui/material';
// Components
import { Button, Form, Select } from 'Components';
import { useFileUpload } from 'Core/hooks';
import { NotificationManager } from 'Components/Notifications';

// Actions
import {
    importDrivers,
    openImportDriversModal,
    resetImportStateDriver,
    clearErrorDriver,
    setFileKeyDriver,
    setDelimiterDriver
} from './redux/slices';
import { HasPermission } from '../../../../../../core/security';
import { searchEntityLists } from 'Redux/actions';

const ModalImportDrivers = () => {
    const { messages } = useIntl();
    
    const delimiters = [
        { id: ',', name: messages['settingsDriver_comma'] },
        { id: ';', name: messages['settingsDriver_semicolon'] }
    ];
    
    const { 
        openImport, 
        loading, 
        importResponse, 
        error
    } = useSelector((state) => state.driversImport);
    const user = useSelector((state) => state.securityRedux.user);
    const entityHosRuleSetList = useSelector((state) => state.driverCreateEditSliceRedux.entityHosRuleSetList);
    
    const dispatch = useDispatch();
    const formRef = useRef();
    const filesUpload = useFileUpload([]);
    
    const [delimiter, setDelimiterLocal] = useState({
        id: '',
        name: messages['drivers_selectDelimiter'] || 'Select'
    });
    const [disableButton, setDisableButton] = useState(false);
    const [errorMessage, setErrorMessage] = useState({
        message: '',
        type: ''
    });

    const handleClose = useCallback(() => {
        dispatch(openImportDriversModal(false));
        dispatch(resetImportStateDriver());
        setErrorMessage({
            message: '',
            type: ''
        });
        setDisableButton(false);
        setDelimiterLocal({
            id: '',
            name: messages['drivers_selectDelimiter'] || 'Select'
    });
    }, [dispatch, messages]);

    useEffect(() => {
        if (importResponse && openImport) {
            setDisableButton(true);
            NotificationManager.success(
                messages['settingsDriver_error_started'] || 'Import started successfully',
                messages['drivers_import_title'] || 'Drivers Import',
                3000,
                null,
                null,
                'success'
            );
            handleClose();
                            }  
    }, [importResponse, openImport, messages, handleClose]);

    useEffect(() => {
        if (error) {
            setErrorMessage({
                message: error,
                type: 'error'
            });
            setDisableButton(false);
        }
    }, [error]);


    useEffect(() => {
        if (openImport && (!entityHosRuleSetList || entityHosRuleSetList.length === 0)) {
            dispatch(searchEntityLists({ terms: ["hosRuleSet"], entityName: "entityName" }));
        }
    }, [openImport, entityHosRuleSetList, dispatch]);

    const handleDelimiterChange = useCallback((selectedDelimiter) => {
        setDelimiterLocal(selectedDelimiter);
        dispatch(setDelimiterDriver(selectedDelimiter.id));
    }, [dispatch]);

    const handleImportSubmit = useCallback(() => {
        if (filesUpload.value.length < 1) {
            setErrorMessage({
                message: messages['settingsDriver_error_required'] || 'File is required',
                type: 'error'
            });
            return false;
        }

        if (!delimiter.id) {
            setErrorMessage({
                message: messages['drivers_error_delimiter'] || 'Delimiter is required',
                type: 'error'
            });
            return false;
        }

        dispatch(clearErrorDriver());
        setErrorMessage({ message: '', type: '' });

        dispatch(setFileKeyDriver(filesUpload.value[0].fileKey));
        dispatch(importDrivers({ 
            fileKey: filesUpload.value[0].fileKey, 
            delimiter: delimiter.id 
        }));
    }, [filesUpload.value, delimiter.id, dispatch, messages]);

    const downloadTemplate = useCallback((e) => {
        e.preventDefault();

        const hasZlogsPermission = Boolean(HasPermission(user, "zlogs"));
        const delimiterSelected = delimiter?.id || ',';

        // Headers must match backend expected fields for ImportDrivers
        const headers = [
            messages['settingsDriver_id'] || 'ID',
            messages['settingsDriver_externalId'] || 'Code',
            messages['settingsDriver_driver'] || 'Driver Name',
            messages['settingsDriver_employeeNumber'] || 'Employee Number',
            messages['settingsDriver_driverPin'] || 'Pin Number',
            messages['settingsDriver_licenseNumber'] || 'License Number',
            messages['settingsDriver_licenseExpDate'] || 'License Expiration Date',
            messages['settingsDriver_registrationCountry'] || 'Registration Country',
            messages['settingsDriver_registrationState'] || 'Registration State',
            messages['settingsDriver_vehicle'] || 'Vehicle',
            messages['settingsDriver_timezone'] || 'Timezone',
            messages['settingsDriver_supervisor'] || 'Supervisor',
            messages['settingsDriver_tags'] || 'Tags',
            messages['settingsDriver_keyFobHex'] || 'KeyFob',
            messages['settingsDriver_email'] || 'Email',
            messages['settingsDriver_phone'] || 'Phone',
        ];

        if (hasZlogsPermission) {
            headers.push(messages['settingsDriver_hosRuleSet'] || 'Hours of Service Ruleset');
            headers.push(messages['settingsDriver_hosExempt'] || 'Hours of Service Exempt');
            headers.push(messages['settingsDriver_hosExemptComment'] || 'Exempt Comment');
            headers.push(messages['settingsDriver_personalConveyance'] || 'Personal Conveyance');
            headers.push(messages['settingsDriver_allowedPCDistance'] || 'Allowed PC Distance');
            headers.push(messages['settingsDriver_personalYardMoves'] || 'Yard Moves');
        }

        headers.push(messages['settingsDriver_status'] || 'Status');

        const eol = "\r\n";

        const csvContent = "\uFEFF" + headers.join(delimiterSelected) + eol;
        const blob = new Blob([csvContent], {
            type: 'text/csv;encoding:utf-8;charset=UTF-8',
        });
        
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.setAttribute('download', 'drivers_template.csv');
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }, [delimiter, messages, user]);

    return (
        <Fragment>
            <Modal
                title={messages['settingsDriver_import_driver']}
                open={openImport}
                loading={loading}
                position="right"
                size="small"
                handleClose={handleClose}
            >
                <ModalBody>
                    <div style={{ padding: '18px 24px 24px 24px', height: 650 }}>
                        {errorMessage.message && (
                            <Alert 
                                severity={errorMessage.type} 
                                onClose={() => setErrorMessage({ message: '', type: '' })}
                            >
                                {(() => {
                                    // Translate backend error codes to user-friendly messages
                                    const errorCode = errorMessage.message.includes('_') 
                                        ? errorMessage.message.split('_')[0] 
                                        : null;
                                    
                                    return errorCode && messages[`drivers_import_error_${errorCode}`]
                                        ? messages[`drivers_import_error_${errorCode}`]
                                        : errorMessage.message;
                                })()}
                            </Alert>
                        )}
                        
                    <Form
                        ref={formRef}
                            onValidSubmit={handleImportSubmit}
                    >
                        <div style={{ padding: 24 }}>
                                <Grid container direction="row" justifyContent="space-between" alignItems="flex-start">
                                    <Grid item sm={12}>
                                        <div style={{ marginBottom: 16, backgroundColor: '#fff3cd', padding: 16, borderRadius: 4, fontWeight: 500 }}>
                                            {messages['drivers_import_instructions']}
                                        </div>
                                    <Button
                                        type="button"
                                        className="btn-blue"
                                            style={{ width: '100%', marginBottom: 16 }}
                                            onClick={downloadTemplate}
                                    >
                                            {messages['drivers_download_template']}
                                    </Button>
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <Select
                                            id="delimiter"
                                            label={messages['settingsDriver_delimiter']}
                                            placeholder={messages['settingsDriver_select_delimiter']}
                                            valueField="id"
                                            textField="name"
                                            options={delimiters}
                                            value={delimiter}
                                            required
                                            onChange={handleDelimiterChange}
                                        />
                                    </Grid>
                                    
                                    <Grid item sm={12}>
                                        <FileUpload
                                            {...filesUpload}
                                            label={`${messages['settingsDriver_file'] || 'File'} (CSV)`}
                                            placeholder={messages['mapLandmarkImport_chooseFile'] || 'Choose file'}
                                            types=".csv"
                                            helpText={messages['mapLandmarkImport_formatSupport'] || 'Only CSV format supported'}
                                            maxSize={5}
                                            disabled={filesUpload?.value?.length > 0 || !delimiter?.id}
                                            maxSizeMessage={`${messages['max'] || 'Max'}: 5MB`}
                                        />
                                        <small className="form-text text-muted">
                                            {messages['mapLandmarkImport_maxSizeFile'] || 'Maximum file size: 5MB'}
                                        </small>
                                    </Grid>
                                </Grid>
                        </div>
                    </Form>
                    </div>
                </ModalBody>
                
                <ModalFooter style={{ bottom: 50, textAlign: 'right' }}>
                    <Button
                        onClick={handleClose}
                        disabled={disableButton}
                        className="btn-cancel"
                    >
                        {messages['drivers_import_cancel'] || 'Cancel'}
                    </Button>

                        <Button
                            onClick={(e) => {
                            if (e) e.preventDefault();
                                formRef.current.submit();
                            }}
                        disabled={disableButton || filesUpload?.value?.length === 0 || !delimiter?.id}
                            className="btn-blue"
                        >
                        {messages['drivers_import_button'] || 'Import'}
                        </Button>
                </ModalFooter>
            </Modal>
        </Fragment>
    );
};

export default ModalImportDrivers;